import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { NodeService } from '../services/NodeService';
import useNodeStore from '../store/useNodeStore';
import useExpertStore from '../store/useExpertStore';
import useContactStore from '../store/useContactStore';
import SpaceDialog from '../components/SpaceDialog';
import DescriptionModal from '../components/DescriptionModal';
import { auth, db, storage } from '../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import DesktopStatus from '../components/layout/DesktopStatus';
import { DesktopService } from '../services/desktopService';
import useAuthStore from '../store/useAuthStore';
import { motion, AnimatePresence } from 'framer-motion';
import useConfigStore from '../store/useConfigStore';
import useChatStore from '../store/useChatStore';
import PromptInput from '../components/chat/PromptInput';
import InstructionMarkdownDialog from '../components/chat/InstructionMarkdownDialog';
import ControlOverlay from '../components/chat/ControlOverlay';
import InviteDialog from '../components/InviteDialog';

const isTauri = () => !!window.__TAURI_INTERNALS__;

// --- Icons ---
const AddIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const BackIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const ConversationIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 8H7M17 12H7" />
    </svg>
);
const FolderIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" /></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>;
const ShieldIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>;
const LockIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>;
const TrashIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>;
const ArrowRightIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.3}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>;
const LaunchIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>;
const EllipsisHorizontalIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>;
const PencilIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>;

const ToggleSwitch = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`${checked ? 'bg-[var(--accent-primary)]' : 'bg-[var(--bg-tertiary)]'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-opacity-75`}
    >
        <span className={`${checked ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
    </button>
);

const LiveInsight = ({ targetUid, targetDeviceId, onExpand }) => {
    const [src, setSrc] = useState(null);
    const [hasFrame, setHasFrame] = useState(false);

    // 0. Auto-start stream on mount
    useEffect(() => {
        if (!targetUid || !targetDeviceId) return;
        DesktopService.sendDesktopCommand(targetUid, targetDeviceId, { command: 'start_stream' })
            .catch(err => console.error('Failed to start stream:', err));
    }, [targetUid, targetDeviceId]);

    // 1. Fetch image whenever Firestore meta updates
    useEffect(() => {
        if (!targetUid || !targetDeviceId) return;
        
        const deviceRef = doc(db, 'users', targetUid, 'devices', targetDeviceId);
        const path = `stream/${targetUid}/${targetDeviceId}/latest.jpg`;
        const storageRef = ref(storage, path);

        const refresh = async () => {
            try {
                const url = await getDownloadURL(storageRef);
                const separator = url.includes('?') ? '&' : '?';
                setSrc(`${url}${separator}t=${Date.now()}`);
                setHasFrame(true);
            } catch (err) { }
        };

        const unsub = onSnapshot(deviceRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().last_frame_at) {
                refresh();
            }
        });

        // Fallback for discovery
        const timer = setTimeout(() => {
            if (!src) refresh();
        }, 3000);

        return () => {
            unsub();
            clearTimeout(timer);
        };
    }, [targetUid, targetDeviceId]);

    return (
        <div className="mb-8 overflow-hidden rounded-2xl border border-[var(--border-strong)] bg-black/40 group relative aspect-video shadow-2xl shadow-black/20">
            {src ? (
                <img src={src} alt="Live Preview" className="w-full h-full object-contain" />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 animate-pulse" />
                    <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest">
                        {hasFrame ? 'Loading Preview...' : 'Waiting for session...'}
                    </span>
                </div>
            )}
            
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-60" />

            {/* Live Indicator */}
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 px-2.5 py-1 rounded-full backdrop-blur-md border border-white/10">
                <div className={`w-1.5 h-1.5 rounded-full ${src ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : 'bg-[var(--text-dim)]'} animate-pulse`} />
                <span className="text-[9px] font-bold text-white tracking-[0.15em] uppercase opacity-90">Live Insight</span>
            </div>

            {/* Expand / Control Action */}
            <div 
                onClick={onExpand}
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer bg-indigo-500/10 backdrop-blur-[2px]"
            >
                <div className="bg-white/10 backdrop-blur-xl px-4 py-2 rounded-2xl border border-white/20 shadow-xl flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                    <span className="text-[10px] font-bold text-white uppercase tracking-widest whitespace-nowrap">Control Desktop</span>
                    <ArrowRightIcon className="w-3.5 h-3.5 text-white" />
                </div>
            </div>
        </div>
    );
};

const Chip = ({ label, onRemove, icon, colorClass = "text-[var(--text-muted)] bg-[var(--bg-tertiary)] border-[var(--border-subtle)]" }) => (
    <div className={`inline-flex items-center gap-1 border rounded-lg px-2.5 py-1 text-xs font-medium ${colorClass}`}>
        {icon && <span className="mr-0.5">{icon}</span>}
        {label}
        {onRemove && (
            <button onClick={onRemove} className="ml-1 hover:text-red-400 focus:outline-none transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
        )}
    </div>
);

const Spinner = ({ className = "w-6 h-6" }) => (
    <svg className={`animate-spin text-[var(--accent-primary)] ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
);

const SparklesIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 00-2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>;
const SendIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" /></svg>;


const NodeDashboardPage = () => {
    const { nodeId } = useParams();
    const navigate = useNavigate();
    const { experts, fetchExperts } = useExpertStore();
    const { contacts, subscribeToContacts } = useContactStore();
    const { profile } = useAuthStore();
    const { aiConfigs } = useConfigStore();
    const { createChat } = useChatStore();

    const [node, setNode] = useState(null);
    const localDeviceId = useAuthStore(state => state.localDeviceId);
    const [children, setChildren] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [ancestry, setAncestry] = useState([]);
    const [visibleAncestry, setVisibleAncestry] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deviceData, setDeviceData] = useState(null);

    const [contextInstructions, setContextInstructions] = useState("");
    const [expertId, setExpertId] = useState("");
    const [desktopEnabled, setDesktopEnabled] = useState(false);
    const [targetDeviceId, setTargetDeviceId] = useState("");
    const [devices, setDevices] = useState([]);
    const [instructionFiles, setInstructionFiles] = useState([]);

    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectedAdmins, setSelectedAdmins] = useState([]);

    const [inheritedMembers, setInheritedMembers] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [folderContext, setFolderContext] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isInstructionDialogOpen, setIsInstructionDialogOpen] = useState(false);
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const [controlMode, setControlMode] = useState(null); // 'antigravity' or 'terminal' or null

    // Hero Chat State
    const [greeting, setGreeting] = useState('');
    const [prompt, setPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedModelConfigId, setSelectedModelConfigId] = useState(null);
    const [selectedExpertId, setSelectedExpertId] = useState(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
    const [editDescriptionDraft, setEditDescriptionDraft] = useState('');
    const textareaRef = useRef(null);
    const menuRef = useRef(null);
    const descriptionEditRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour < 12) setGreeting('Good morning');
        else if (hour < 18) setGreeting('Good afternoon');
        else setGreeting('Good evening');
    }, []);

    // Listen to target device changes specifically for iTerm and streaming state
    useEffect(() => {
        if (!node?.settings?.targetDeviceId || !node?.ownerId) return;
        const deviceRef = doc(db, 'users', node.ownerId, 'devices', node.settings.targetDeviceId);
        const unsub = onSnapshot(deviceRef, (docSnap) => {
            if (docSnap.exists()) {
                setDeviceData(docSnap.data());
            }
        });
        return () => unsub();
    }, [node?.settings?.targetDeviceId, auth.currentUser?.uid]);

    useEffect(() => {
        if (aiConfigs?.defaultConfigId && !selectedModelConfigId) {
            setSelectedModelConfigId(aiConfigs.defaultConfigId);
        }
    }, [aiConfigs, selectedModelConfigId]);


    const handleHeroSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!prompt.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const title = prompt.trim().split(' ').slice(0, 4).join(' ') + (prompt.split(' ').length > 4 ? '...' : '');

            const chatSettings = {
                targetDeviceId: node?.settings?.targetDeviceId || null,
                desktopEnabled: node?.settings?.desktopEnabled || false,
                expertId: selectedExpertId
            };

            if (typeof selectedModelConfigId === 'string') {
                chatSettings.modelConfigId = selectedModelConfigId;
                chatSettings.localBrainEnabled = false;
                chatSettings.localModel = null;
            } else if (selectedModelConfigId?.provider === 'ollama') {
                chatSettings.modelConfigId = null;
                chatSettings.localBrainEnabled = true;
                chatSettings.localModel = selectedModelConfigId.model;
            }

            const newSessionId = await createChat(title, chatSettings, nodeId);
            navigate(`/chat/${newSessionId}`, { state: { initialMessage: prompt.trim() } });
        } catch (err) {
            console.error("Failed to start hero chat:", err);
            setIsSubmitting(false);
        }
    };

    const handleExpertSelect = (newExpertId) => {
        setSelectedExpertId(newExpertId);
    };

    const handleModelSelect = (newModelConfigId) => {
        setSelectedModelConfigId(newModelConfigId);
    };


    useEffect(() => {
        fetchExperts();
        const unsub = subscribeToContacts();
        loadData();
        return () => { if (unsub) unsub(); };
    }, [nodeId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [n, c, s, a] = await Promise.all([
                NodeService.getNode(nodeId),
                NodeService.getChildNodes(nodeId, auth.currentUser?.uid),
                NodeService.getNodeSessions(nodeId, auth.currentUser?.uid),
                NodeService.getNodeAncestry(nodeId)
            ]);

            setNode(n);
            setChildren(c);
            setSessions(s);
            setAncestry(a);

            const currentUid = auth.currentUser?.uid;
            const visible = a.filter(ancestor =>
                ancestor.id !== n.id &&
                (ancestor.members?.includes(currentUid) || ancestor.ownerId === currentUid)
            );
            setVisibleAncestry(visible);

            const explicitMembers = n?.members || [];
            const explicitAdmins = n?.admins || [];

            setSelectedMembers(explicitMembers);
            setSelectedAdmins(explicitAdmins);

            const isOwner = n.ownerId === currentUid;
            const isExplicitAdmin = explicitAdmins.includes(currentUid);
            let adminPower = isOwner || isExplicitAdmin;

            if (!adminPower) {
                a.some(ancestor => {
                    if (ancestor.admins?.includes(currentUid) || ancestor.ownerId === currentUid) {
                        adminPower = true;
                        return true;
                    }
                    return false;
                });
            }
            setIsAdmin(adminPower);

            // Fetch Instruction Files
            if (n.settings?.instructionFileIds?.length > 0) {
                const filePromises = n.settings.instructionFileIds.map(id => NodeService.getNode(id));
                const files = await Promise.all(filePromises);
                setInstructionFiles(files.filter(f => f !== null));
            } else {
                setInstructionFiles([]);
            }

        } catch (err) {
            console.error("Error loading dashboard:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSettings = () => {
        setDialogMode('edit');
        setIsDialogOpen(true);
    };

    const handleCreateSubSpace = () => {
        setDialogMode('create');
        setIsDialogOpen(true);
    };

    const handleCreateSession = async () => {
        try {
            const initialSessionSettings = {
                desktopEnabled: node?.settings?.desktopEnabled || false
            };
            const newId = await NodeService.createSession(auth.currentUser.uid, nodeId, "New Chat", initialSessionSettings);
            navigate(`/chat/${newId}`);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteSession = async (sessionId, e) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to delete this conversation?")) {
            try {
                await NodeService.deleteSession(sessionId);
                setSessions(prev => prev.filter(s => s.id !== sessionId));
            } catch (err) {
                console.error(err);
                alert("Failed to delete conversation.");
            }
        }
    };

    const handleBack = () => {
        if (visibleAncestry.length > 0) {
            const nearest = visibleAncestry[visibleAncestry.length - 1];
            navigate(`/space/${nearest.id}`);
        } else {
            navigate('/projects');
        }
    };

    const getMemberName = (uid) => {
        if (uid === auth.currentUser?.uid) return "Me (You)";
        const contact = contacts.find(c => c.uid === uid);
        return contact?.displayName || contact?.email || "Unknown User";
    };


    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this space? This action cannot be undone.")) return;
        try {
            await NodeService.deleteNode(nodeId);
            navigate('/projects');
        } catch (err) {
            console.error(err);
            alert("Failed to delete space.");
        }
    };

    const handleStartEditDescription = () => {
        if (!isAdmin) return;
        setIsDescriptionModalOpen(true);
    };

    const handleSaveDescription = async () => {
        setIsSaving(true);
        try {
            const newSettings = {
                ...(node.settings || {}),
                customInstructions: editDescriptionDraft
            };
            await NodeService.updateNodeSettings(nodeId, newSettings);
            setNode({ ...node, settings: newSettings });
            setIsEditingDescription(false);
        } catch (err) {
            console.error("Failed to save description:", err);
            alert("Failed to save description.");
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="h-screen bg-[var(--bg-main)] flex items-center justify-center"><Spinner /></div>;
    if (!node) return <div className="h-screen bg-[var(--bg-main)] text-[var(--text-muted)] p-8">Space not found.</div>;

    return (
        <div className="h-screen flex flex-col bg-[var(--bg-main)] text-[var(--text-main)] font-sans overflow-hidden transition-colors duration-300 relative">

            {/* Background Aesthetic Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-[var(--accent-primary)]/20 blur-[120px]" />
                <div className="absolute -bottom-[10%] left-[20%] w-[45%] h-[45%] rounded-full bg-indigo-500/10 blur-[130px]" />
            </div>

            {/* Main Content Area */}
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden relative z-10 w-full lg:justify-center">

                {/* CENTER: Hero Chat + Conversations */}
                <div className="w-full lg:max-w-4xl lg:overflow-y-auto bg-transparent custom-scrollbar flex flex-col items-center flex-1">

                    <div className="w-full max-w-4xl px-4 md:px-8 py-8 md:py-12 flex flex-col items-start min-h-fit lg:min-h-screen">

                        {/* Back Arrow & Breadcrumbs */}
                        <div className="flex items-center gap-3 mb-6 md:mb-8 animate-in fade-in slide-in-from-top-4 duration-500 w-full overflow-hidden">
                            <button
                                onClick={handleBack}
                                className="flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors cursor-pointer group shrink-0"
                            >
                                <BackIcon className="w-4 h-4" />
                            </button>

                            <nav className="flex items-center text-sm font-medium text-[var(--text-muted)]/70 tracking-tight overflow-x-auto no-scrollbar whitespace-nowrap pb-1">
                                <button onClick={() => navigate('/projects')} className="hover:text-[var(--accent-primary)] transition-colors">Spaces</button>
                                {visibleAncestry.map(ancestor => (
                                    <React.Fragment key={ancestor.id}>
                                        <span className="mx-2.5 text-indigo-500/80 font-bold">·</span>
                                        <button onClick={() => navigate(`/space/${ancestor.id}`)} className="hover:text-[var(--accent-primary)] transition-colors truncate max-w-[120px]">
                                            {ancestor.title}
                                        </button>
                                    </React.Fragment>
                                ))}
                                <span className="mx-2.5 text-indigo-500/80 font-bold">·</span>
                                <span className="text-[var(--text-main)] font-medium truncate max-w-[240px] tracking-normal opacity-40">
                                    {node.title}
                                </span>
                            </nav>
                        </div>

                        {/* Space Header & Description */}
                        <div className="mb-8 md:mb-10 w-full animate-in slide-in-from-bottom-4 duration-500 fade-in text-left">
                            <div className="flex items-start justify-between w-full gap-4">
                                <h1 className="text-2xl md:text-3xl lg:text-4xl font-light text-[var(--text-main)] tracking-tighter leading-tight flex-1 transition-all">
                                    {node.title}
                                </h1>
                                {isAdmin && (
                                    <div className="relative mt-2" ref={menuRef}>
                                        <button
                                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                                            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] transition-all"
                                        >
                                            <EllipsisHorizontalIcon className="w-5 h-5" />
                                        </button>

                                        <AnimatePresence>
                                            {isMenuOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                    className="absolute right-0 mt-2 w-48 bg-[var(--bg-secondary)] shadow-2xl z-50 overflow-hidden rounded-2xl border border-[var(--border-strong)]"
                                                >
                                                    <div className="py-1">
                                                        <button
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                                handleOpenSettings();
                                                            }}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 transition-colors"
                                                        >
                                                            <PencilIcon className="w-4 h-4 opacity-70" />
                                                            Space settings
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                                handleDelete();
                                                            }}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                            Delete Space
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </div>

                            {false ? ( // Replaced with Modal
                                <div className="w-full max-w-3xl mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <textarea
                                        autoFocus
                                        value={editDescriptionDraft}
                                        onChange={(e) => setEditDescriptionDraft(e.target.value)}
                                        placeholder="Enter a description for this space..."
                                        className="w-full bg-transparent border-none text-[var(--text-main)] text-sm rounded-none px-0 py-2 outline-none transition-all resize-none font-light leading-relaxed min-h-[100px]"
                                    />
                                    <div className="flex items-center gap-3 mt-3">
                                        <button
                                            onClick={handleSaveDescription}
                                            disabled={isSaving}
                                            className="px-5 py-2 bg-[var(--accent-primary)] text-[var(--bg-main)] rounded-xl text-sm font-bold hover:bg-[var(--accent-hover)] transition-all flex items-center gap-2"
                                        >
                                            {isSaving && <Spinner className="w-3 h-3 text-inherit" />}
                                            Save Description
                                        </button>
                                        <button
                                            onClick={() => setIsEditingDescription(false)}
                                            className="px-5 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] text-sm font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="max-w-3xl mt-4 group relative">
                                    {node.settings?.customInstructions ? (
                                        <>
                                            <p className={`text-sm text-[var(--text-muted)]/80 leading-relaxed font-light tracking-wide transition-all duration-300 ${!isDescriptionExpanded ? 'line-clamp-2' : ''}`}>
                                                {node.settings.customInstructions}
                                            </p>
                                            <div className="flex items-center gap-4 mt-3">
                                                <button
                                                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                                    className="text-[11px] font-bold text-indigo-400/80 hover:text-indigo-300 uppercase tracking-widest transition-colors flex items-center gap-1"
                                                >
                                                    {isDescriptionExpanded ? 'Show Less' : 'Show More'}
                                                    <svg className={`w-3 h-3 transition-transform duration-300 ${isDescriptionExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        onClick={handleStartEditDescription}
                                                        className="text-[11px] font-bold text-[var(--text-dim)] hover:text-[var(--text-main)] uppercase tracking-widest transition-colors opacity-0 group-hover:opacity-100 flex items-center gap-1"
                                                    >
                                                        <PencilIcon className="w-3 h-3" />
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </ >
                                    ) : (
                                        <div className="flex flex-col items-start gap-1">
                                            <p className="text-sm text-[var(--text-dim)]/15 italic font-light tracking-wide leading-relaxed">
                                                There is currently no description for this project.
                                            </p>
                                            {isAdmin && (
                                                <button
                                                    onClick={handleStartEditDescription}
                                                    className="text-[11px] font-bold text-indigo-400/60 hover:text-indigo-400 uppercase tracking-widest transition-all hover:translate-x-0.5 flex items-center gap-1.5"
                                                >
                                                    <AddIcon className="w-3 h-3" />
                                                    add description
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <PromptInput
                            value={prompt}
                            onChange={setPrompt}
                            onSubmit={handleHeroSubmit}
                            placeholder={`Message in ${node.title}...`}
                            isSubmitting={isSubmitting}
                            selectedModelConfigId={selectedModelConfigId}
                            onModelSelect={handleModelSelect}
                            selectedExpertId={selectedExpertId}
                            onExpertSelect={handleExpertSelect}
                            containerClassName="mb-8 md:mb-10 animate-in slide-in-from-bottom-6 duration-700 fade-in delay-150 fill-mode-both"
                        />

                        {/* All Conversations */}
                        <div className="w-full">
                            <div className="grid grid-cols-1 w-full">
                                {sessions.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-[var(--border-subtle)] rounded-2xl mt-4">
                                        <ConversationIcon className="w-8 h-8 text-[var(--text-dim)]/30 mb-3" />
                                        <h3 className="text-sm font-medium text-[var(--text-muted)]">No conversations here yet</h3>
                                        <p className="text-xs text-[var(--text-dim)] mt-1">Start your first conversation above!</p>
                                    </div>
                                ) : (
                                    <AnimatePresence mode='popLayout'>
                                        {sessions.map((session, idx) => (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.05 }}
                                                key={session.id}
                                                onClick={() => navigate(`/chat/${session.id}`)}
                                                className="pl-10 pr-8 py-5 bg-[var(--bg-secondary)] border border-white/[0.03] rounded-2xl mb-3 cursor-pointer transition-all flex flex-row items-center justify-between group relative hover:bg-[var(--bg-tertiary)] hover:border-white/[0.1] hover:shadow-sm"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-[14px] font-light text-[var(--text-main)] truncate tracking-tight transition-colors tracking-[0.02em]">{session.title || "Untitled Conversation"}</h3>
                                                    <p className="text-[9px] text-[var(--text-dim)] mt-1.5 font-bold uppercase tracking-widest opacity-40">
                                                        {session.lastActive ? `Last active ${new Date(session.lastActive.seconds * 1000).toLocaleDateString()}` : 'New'}
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <div className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 transform flex items-center justify-center">
                                                        <ArrowRightIcon className="w-3.5 h-3.5 text-[var(--text-muted)] opacity-60 group-hover:opacity-100 transition-opacity" />
                                                    </div>
                                                    {isAdmin && (
                                                        <button
                                                            onClick={(e) => handleDeleteSession(session.id, e)}
                                                            className="p-1.5 opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-red-400 transition-all rounded-lg hover:bg-red-500/10"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDEBAR: Project Content (Sub-Nodes) */}
                <div className="w-full lg:w-[340px] flex flex-col shrink-0 px-4 md:px-6 py-8 lg:py-12">
                    <div className="bg-transparent border border-[var(--border-strong)] rounded-2xl flex flex-col overflow-hidden h-fit max-h-[calc(100vh-100px)]">

                        {/* Unified Top Banner for Right Column */}
                        <div className="p-6 border-b border-[var(--border-strong)]">
                            {node.settings?.targetDeviceId && (
                                <LiveInsight 
                                    targetUid={node.ownerId} 
                                    targetDeviceId={node.settings.targetDeviceId} 
                                    onExpand={() => setControlMode('live_insight')}
                                />
                            )}
                            <div className="flex items-center justify-between">
                                <h2 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-[0.2em] flex items-center gap-2">
                                    Instructions
                                </h2>
                                <div className="flex items-center gap-1">
                                    {isAdmin && (
                                        <button
                                            onClick={() => setIsInstructionDialogOpen(true)}
                                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                                            title="Add Instructions"
                                        >
                                            <AddIcon className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => setIsInviteDialogOpen(true)}
                                            className="p-1.5 rounded-lg text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-colors"
                                            title="Invite Friends"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-[var(--text-main)]/80 mt-3 italic line-clamp-3 leading-relaxed">
                                {node.settings?.customInstructions || "No secondary project brain set."}
                            </p>

                            {/* Instruction Files List */}
                            {instructionFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    <p className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-wider mb-2">Instruction Documents</p>
                                    {instructionFiles.map(file => (
                                        <div key={file.id} className="flex items-center gap-2.5 p-2 rounded-lg bg-[var(--text-muted)]/5 border border-[var(--border-subtle)] group hover:bg-[var(--text-muted)]/10 transition-all cursor-pointer">
                                            <div className="w-7 h-7 rounded bg-indigo-500/10 flex items-center justify-center">
                                                <svg className="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2.008 0 01-2-2.008V5.008C5 3.9 5.9 3 7.008 3h10.984C19.1 3 20 3.9 20 5.008V18.1a2.9 2.9 0 01-2.9 2.9z" /></svg>
                                            </div>
                                            <span className="text-[11px] font-medium text-[var(--text-main)]/90 truncate flex-1">
                                                {file.title}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-6">
                            {/* App-Specific Remote Control Actions */}
                            {node.settings?.desktopEnabled && node.settings?.targetDeviceId && (
                                <div className="mb-6 animate-in fade-in slide-in-from-right-4 duration-500 delay-100">
                                    <h2 className="text-[10px] font-bold text-[var(--accent-primary)] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse" />
                                        Remote Control
                                    </h2>
                                    <div className="flex flex-col gap-2">
                                        <button
                                            onClick={() => setControlMode('antigravity')}
                                            className="w-full relative overflow-hidden group bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 hover:border-indigo-500/40 rounded-xl p-3 flex items-center gap-3 transition-all"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/10 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:translate-x-full duration-700 transition-all" />
                                            <div className="w-8 h-8 rounded-lg bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] flex items-center justify-center shrink-0">
                                                <SparklesIcon className="w-4 h-4" />
                                            </div>
                                            <div className="text-left flex-1 min-w-0">
                                                <h4 className="text-xs font-bold text-[var(--text-main)] truncate">Antigravity Mode</h4>
                                                <p className="text-[10px] text-[var(--text-dim)] truncate">Vibe code your UI automatically</p>
                                            </div>
                                            <LaunchIcon className="w-4 h-4 text-[var(--text-muted)] opacity-50 group-hover:opacity-100 group-hover:text-[var(--accent-primary)] transition-all" />
                                        </button>

                                        <button
                                            onClick={() => setControlMode('vscode')}
                                            className="w-full relative overflow-hidden group bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 hover:border-blue-500/40 rounded-xl p-3 flex items-center gap-3 transition-all"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-cyan-500/0 opacity-0 group-hover:opacity-100 group-hover:translate-x-full duration-700 transition-all" />
                                            <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-500 flex items-center justify-center shrink-0">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L8.499 15.3l-8.173 6.56a1 1 0 0 0 .001 1.479l1.322 1.202a1 1 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 21.413V2.587a1.5 1.5 0 0 0-.85-1.37zm-6.07 14.821L11.51 12.5l5.57-4.908v9.816z" /></svg>
                                            </div>
                                            <div className="text-left flex-1 min-w-0">
                                                <h4 className="text-xs font-bold text-[var(--text-main)] truncate">VS Code Mode</h4>
                                                <p className="text-[10px] text-[var(--text-dim)] truncate">Control Roo Code remotely</p>
                                            </div>
                                            <LaunchIcon className="w-4 h-4 text-[var(--text-muted)] opacity-50 group-hover:opacity-100 group-hover:text-blue-500 transition-all" />
                                        </button>

                                        <button
                                            onClick={() => setControlMode('iterm')}
                                            className="w-full relative overflow-hidden group bg-green-500/5 border border-green-500/10 hover:border-green-500/30 rounded-xl p-3 flex items-center gap-3 transition-all"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-green-500/5 to-green-500/0 opacity-0 group-hover:opacity-100 group-hover:translate-x-full duration-700 transition-all" />
                                            <div className="w-8 h-8 rounded-lg bg-green-500/10 text-green-500 flex items-center justify-center shrink-0">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 0 00-2 2v12a2 0 002 2z" /></svg>
                                            </div>
                                            <div className="text-left flex-1 min-w-0">
                                                <h4 className="text-xs font-bold text-[var(--text-main)] truncate">Run iTerm Session</h4>
                                                <p className="text-[10px] text-[var(--text-dim)] truncate">Open iTerm & cd to project</p>
                                            </div>
                                            <LaunchIcon className="w-4 h-4 text-[var(--text-muted)] opacity-50 group-hover:opacity-100 group-hover:text-green-500 transition-all" />
                                        </button>

                                        <button
                                            onClick={() => setControlMode('terminal')}
                                            className="w-full relative overflow-hidden group bg-black/40 border border-white/5 hover:border-[var(--text-main)]/20 rounded-xl p-3 flex items-center gap-3 transition-all grayscale opacity-50 hover:grayscale-0 hover:opacity-100"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-muted)] flex items-center justify-center shrink-0 border border-[var(--border-subtle)]">
                                                <span className="font-mono text-xs font-bold">{'>_'}</span>
                                            </div>
                                            <div className="text-left flex-1 min-w-0">
                                                <h4 className="text-xs font-bold text-[var(--text-main)] truncate">Classic Terminal</h4>
                                                <p className="text-[10px] text-[var(--text-dim)] truncate">Raw background shell</p>
                                            </div>
                                            <LaunchIcon className="w-4 h-4 text-[var(--text-muted)] opacity-50 group-hover:opacity-100 transition-all" />
                                        </button>

                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between">
                                <h2 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-[0.2em] flex items-center gap-2">
                                    Sub-Spaces
                                </h2>
                                {isAdmin && (
                                    <button
                                        onClick={handleCreateSubSpace}
                                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                                        title="Add Sub-Space"
                                    >
                                        <AddIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                            <div className="space-y-2">
                                {children.length === 0 ? (
                                    <div className="text-center py-12 px-6 flex flex-col items-center">
                                        <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-4 border border-dashed border-white/10">
                                            <FolderIcon className="w-6 h-6 text-white/20" />
                                        </div>
                                        <p className="text-xs text-[var(--text-dim)] font-medium leading-relaxed">
                                            No linked files or sub-spaces yet.
                                        </p>
                                        {isAdmin && (
                                            <button
                                                onClick={handleCreateSubSpace}
                                                className="mt-4 text-[10px] font-bold text-indigo-400 hover:text-indigo-300 uppercase tracking-widest"
                                            >
                                                + Add Content
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <AnimatePresence mode='popLayout'>
                                        {children.map((child, idx) => (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.05 }}
                                                key={child.id}
                                                onClick={() => navigate(`/space/${child.id}`)}
                                                className="pl-8 pr-6 py-5 bg-[var(--bg-secondary)] border border-white/[0.03] rounded-2xl mb-3 cursor-pointer transition-all flex flex-row items-center justify-between group relative hover:border-white/[0.08] hover:shadow-sm"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-[14px] font-light text-[var(--text-main)] truncate tracking-tight transition-colors tracking-[0.02em]">
                                                        {child.title}
                                                    </h3>
                                                    <p className="text-[9px] text-[var(--text-dim)] mt-1.5 font-bold uppercase tracking-widest opacity-40">
                                                        Updated {child.updatedAt ? new Date(child.updatedAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <div className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 transform flex items-center justify-center">
                                                        <ArrowRightIcon className="w-3.5 h-3.5 text-[var(--text-muted)] opacity-60 group-hover:opacity-100 transition-opacity" />
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SpaceDialog
                open={isDialogOpen}
                onClose={() => { setIsDialogOpen(false); loadData(); }}
                parentId={nodeId}
                mode={dialogMode}
                nodeId={nodeId}
            />

            <DescriptionModal
                isOpen={isDescriptionModalOpen}
                onClose={() => {
                    setIsDescriptionModalOpen(false);
                    loadData();
                }}
                nodeId={nodeId}
                initialDescription={node.settings?.customInstructions}
                userId={auth.currentUser?.uid}
                deviceId={node.settings?.targetDeviceId}
                folderPath={node.settings?.path}
            />

            <InstructionMarkdownDialog
                open={isInstructionDialogOpen}
                onClose={() => setIsInstructionDialogOpen(false)}
                parentId={nodeId}
                onSaved={loadData}
            />            <ControlOverlay
                open={!!controlMode}
                onClose={() => setControlMode(null)}
                mode={controlMode}
                targetDeviceId={node.settings?.targetDeviceId}
                targetUid={node.ownerId}
                folderPath={node.settings?.path}
                nodeId={node.id}
                deviceData={deviceData}
            />

            <InviteDialog
                open={isInviteDialogOpen}
                onClose={() => setIsInviteDialogOpen(false)}
                nodeId={nodeId}
            />
        </div>
    );
};

export default NodeDashboardPage;
